import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '../../../components/ui/Button.js';
import { Card } from '../../../components/ui/Card.js';
import { FormField, Input, Textarea } from '../../../components/ui/FormField.js';
import { PageHeader } from '../../../components/ui/PageHeader.js';
import { useBackNavigation } from '../../../hooks/useBackNavigation.js';
import { useSaveMutation } from '../../../hooks/useSaveMutation.js';
import { api } from '../../../services/api.js';

interface GiftCertificateFormData {
  face_value: string; // major units (e.g. "500.00")
  code: string;
  expires_at: string; // YYYY-MM-DD
  recipient_email: string;
  note: string;
}

const EMPTY: GiftCertificateFormData = {
  face_value: '',
  code: '',
  expires_at: '',
  recipient_email: '',
  note: '',
};

export function GiftCertificateCreateView() {
  const navigate = useNavigate();
  const goBack = useBackNavigation('/gift-certificates');
  const [form, setForm] = useState<GiftCertificateFormData>(EMPTY);
  const [error, setError] = useState('');

  // 161 (US2): issuing a certificate invalidated nothing, so the list did not contain it (FR-003).
  const { save, isPending } = useSaveMutation({
    resource: 'giftCertificate',
    mutationFn: () => {
      // Face value is entered in major units (RM); the API expects minor units.
      const payload: Record<string, unknown> = {
        initial_value_minor: Math.round(Number(form.face_value) * 100),
        code: form.code.trim() || undefined,
        expires_at: form.expires_at
          ? new Date(`${form.expires_at}T23:59:59.999Z`).toISOString()
          : undefined,
        recipient_email: form.recipient_email.trim() || undefined,
        note: form.note.trim() || undefined,
      };
      return api.post<{ _id: string }>('/gift-certificates', payload);
    },
    onSaved: () => navigate('/gift-certificates'),
    onError: (err) =>
      setError((err as { message?: string }).message ?? 'Failed to issue gift certificate'),
  });

  function set<K extends keyof GiftCertificateFormData>(k: K, v: GiftCertificateFormData[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  const faceValueNumber = Number(form.face_value);
  const faceValueValid = form.face_value !== '' && faceValueNumber > 0;

  return (
    <div>
      <PageHeader
        title="Issue Gift Certificate"
        description="Issue a new gift certificate with a fixed face value."
        fallback="/gift-certificates"
      />
      <form
        className="max-w-2xl space-y-6"
        onSubmit={(e) => {
          e.preventDefault();
          setError('');
          if (!faceValueValid) {
            setError('Face value must be greater than 0.');
            return;
          }
          void save();
        }}
      >
        <Card>
          <h2 className="mb-4 text-sm font-semibold text-ink">Certificate</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField
              label="Face Value"
              name="face_value"
              required
              hint="In RM, e.g. 500.00. Converted to minor units on save."
            >
              <Input
                id="face_value"
                type="number"
                min={0}
                step="0.01"
                value={form.face_value}
                onInput={(e) => set('face_value', (e.target as HTMLInputElement).value)}
                required
              />
            </FormField>
            <FormField
              label="Code"
              name="code"
              hint="Optional. Auto-generated if blank. Uppercased on save."
            >
              <Input
                id="code"
                value={form.code}
                onInput={(e) => set('code', (e.target as HTMLInputElement).value)}
              />
            </FormField>
            <FormField label="Expires At" name="expires_at" hint="Optional expiry date.">
              <Input
                id="expires_at"
                type="date"
                value={form.expires_at}
                onInput={(e) => set('expires_at', (e.target as HTMLInputElement).value)}
              />
            </FormField>
          </div>
        </Card>

        <Card>
          <h2 className="mb-4 text-sm font-semibold text-ink">Recipient (optional)</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {/* 358 (FR-027): a recipient email locks the certificate to that customer's account (#1091). */}
            <FormField
              label="Recipient Email"
              name="recipient_email"
              hint="Only the account with this email can use it, once that email is verified. Leave it empty for a code anyone can use."
            >
              <Input
                id="recipient_email"
                type="email"
                value={form.recipient_email}
                onInput={(e) => set('recipient_email', (e.target as HTMLInputElement).value)}
              />
            </FormField>
            <FormField label="Note" name="note" hint="Internal note, e.g. reason for issue.">
              <Textarea
                id="note"
                rows={2}
                value={form.note}
                onInput={(e) => set('note', (e.target as HTMLTextAreaElement).value)}
              />
            </FormField>
          </div>
        </Card>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex gap-3">
          <Button type="submit" loading={isPending}>
            {isPending ? 'Issuing…' : 'Issue Certificate'}
          </Button>
          <Button type="button" variant="ghost" onClick={goBack}>
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}
